import * as React from 'react';
import { useFormContext, ControllerRenderProps, ControllerFieldState } from 'react-hook-form';

import { cn } from '@epoch-ui/lib/utils';
import { generateTimeOptions } from '@epoch-ui/lib/time.utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@epoch-ui/components/Select';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormDescription
} from '@epoch-ui/components/Form/form';

export interface TimePickerFieldProps {
  name: string;
  label?: string | React.ReactNode | React.ReactNode[];
  className?: string;
  description?: string | React.ReactNode | React.ReactNode[];
  placeholder?: string;
  interval?: 5 | 15 | 30 | 60;
  disabled?: boolean;
  defaultTime?: string;
}

interface TimePickerFieldInnerProps extends Omit<TimePickerFieldProps, 'name' | 'defaultTime'> {
  timeOptions: { value: string; label: string }[];
  getRoundedDefaultTime: () => string;
  forwardedRef: React.Ref<HTMLButtonElement>;
  defaultTimeFromProps?: string;
  field: ControllerRenderProps<any, string>;
  fieldState: ControllerFieldState;
}

const TimePickerFieldInner: React.FC<TimePickerFieldInnerProps> = ({
  label,
  description,
  className,
  placeholder = 'Select time',
  disabled = false,
  timeOptions,
  getRoundedDefaultTime,
  forwardedRef,
  defaultTimeFromProps,
  field,
  fieldState
}) => {
  React.useEffect(() => {
    if (field.value === undefined) {
      let chosenTime: string | undefined;

      if (
        defaultTimeFromProps &&
        timeOptions.some((option) => option.value === defaultTimeFromProps)
      ) {
        chosenTime = defaultTimeFromProps;
      } else {
        chosenTime = getRoundedDefaultTime();
      }

      if (timeOptions.some((option) => option.value === chosenTime)) {
        field.onChange(chosenTime);
      } else if (timeOptions.length > 0) {
        field.onChange(timeOptions[0].value);
      }
    }
  }, [field, timeOptions, getRoundedDefaultTime, defaultTimeFromProps]);

  return (
    <FormItem className={cn('tw-mt-4', className)}>
      {label && <FormLabel>{label}</FormLabel>}
      {description && <FormDescription>{description}</FormDescription>}
      <Select
        onValueChange={field.onChange}
        value={field.value as string | undefined}
        disabled={disabled}
      >
        <FormControl>
          <SelectTrigger
            ref={forwardedRef}
            className={cn(
              !field.value && 'tw-text-muted-foreground',
              'tw-h-[48px] tw-w-[134px]',
              fieldState.error &&
                'tw-border-destructive-foreground tw-ring-destructive-foreground focus:tw-border-destructive-foreground'
            )}
          >
            <SelectValue placeholder={<span>{placeholder}</span>} />
          </SelectTrigger>
        </FormControl>
        <SelectContent>
          {timeOptions.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FormItem>
  );
};

export const TimePickerField = React.forwardRef<HTMLButtonElement, TimePickerFieldProps>(
  (
    {
      name,
      label,
      description,
      className,
      placeholder = 'Select time',
      interval = 5,
      disabled = false,
      defaultTime
    },
    ref
  ) => {
    const { control } = useFormContext();
    const timeOptions = React.useMemo(() => generateTimeOptions(interval), [interval]);

    const getRoundedDefaultTime = React.useCallback((): string => {
      const now = new Date();
      let hour = now.getHours();
      const minute = now.getMinutes();
      // Round up to the nearest interval
      const rounded = (Math.ceil(minute / interval) * interval) % 60;
      if (rounded === 0 && minute !== 0) {
        hour = (hour + 1) % 24;
      }
      const defaultHourStr = String(hour).padStart(2, '0');
      let bestMatch = `${defaultHourStr}:${String(rounded).padStart(2, '0')}`;

      if (!timeOptions.some((opt) => opt.value === bestMatch)) {
        const firstOptionForHour = timeOptions.find((opt) =>
          opt.value.startsWith(`${defaultHourStr}:`)
        );
        if (firstOptionForHour) {
          bestMatch = firstOptionForHour.value;
        } else if (timeOptions.length > 0) {
          bestMatch = timeOptions[0].value;
        }
      }
      return bestMatch;
    }, [timeOptions, interval]);

    return (
      <FormField
        control={control}
        name={name}
        render={({ field, fieldState }): React.ReactElement => (
          <TimePickerFieldInner
            label={label}
            description={description}
            className={className}
            placeholder={placeholder}
            disabled={disabled}
            timeOptions={timeOptions}
            getRoundedDefaultTime={getRoundedDefaultTime}
            interval={interval}
            forwardedRef={ref}
            defaultTimeFromProps={defaultTime}
            field={field}
            fieldState={fieldState}
          />
        )}
      />
    );
  }
);

TimePickerField.displayName = 'TimePickerField';
